import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getShare, getComments, addComment } from '../services/shareApi';
import { FloorPlanCanvas } from '../components/canvas';
import { useLayers } from '../hooks/useLayers';

/**
 * ShareViewer - Read-only viewer for shared floor plans
 */
const ShareViewer = () => {
  const { shareId } = useParams();
  const canvasRef = useRef(null);

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [comments, setComments] = useState([]);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentPosition, setCommentPosition] = useState(null);
  const [isAddingComment, setIsAddingComment] = useState(false);

  // Canvas state
  const [scale, setScale] = useState(0.5);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState(null);

  // Layers (use defaults)
  const { layers } = useLayers();

  // Load share data on mount
  useEffect(() => {
    const loadShare = async () => {
      try {
        setLoading(true);
        const data = await getShare(shareId);
        setShareData(data);

        // Load comments if enabled
        if (data.allowComments) {
          const commentsData = await getComments(shareId);
          setComments(commentsData.comments || []);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (shareId) {
      loadShare();
    }
  }, [shareId]);

  // Get active floor from project data
  const activeFloor = shareData?.projectData?.floors?.[0] || null;

  // Handle wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    setScale((prev) => Math.min(Math.max(prev * zoomFactor, 0.1), 5));
  }, []);

  // Handle pointer events for panning
  const handlePointerDown = useCallback((e) => {
    if (isAddingComment) {
      // Get canvas position for comment
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        const x = (e.clientX - rect.left - offset.x) / scale;
        const y = (e.clientY - rect.top - offset.y) / scale;
        setCommentPosition({ x, y });
        setShowCommentModal(true);
        setIsAddingComment(false);
      }
      return;
    }

    setIsPanning(true);
    setPanStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  }, [isAddingComment, offset, scale]);

  const handlePointerMove = useCallback((e) => {
    if (!isPanning || !panStart) return;
    setOffset({
      x: e.clientX - panStart.x,
      y: e.clientY - panStart.y,
    });
  }, [isPanning, panStart]);

  const handlePointerUp = useCallback(() => {
    setIsPanning(false);
    setPanStart(null);
  }, []);

  // Handle adding a comment
  const handleAddComment = async (authorName, commentText) => {
    if (!commentPosition) return;

    try {
      await addComment(shareId, {
        authorName: authorName || 'Anonymous',
        commentText,
        positionX: commentPosition.x,
        positionY: commentPosition.y,
      });

      // Reload comments
      const commentsData = await getComments(shareId);
      setComments(commentsData.comments || []);
      setShowCommentModal(false);
      setCommentPosition(null);
    } catch (err) {
      alert('Failed to add comment: ' + err.message);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingBox}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Loading floor plan...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorBox}>
          <span style={styles.errorIcon}>!</span>
          <h2 style={styles.errorTitle}>
            {error === 'Share not found' ? 'Floor Plan Not Found' :
             error === 'Share has expired' ? 'Link Expired' :
             'Error Loading Floor Plan'}
          </h2>
          <p style={styles.errorMessage}>
            {error === 'Share not found' ?
              'This floor plan link is invalid or has been deleted.' :
             error === 'Share has expired' ?
              'This shared link has expired and is no longer available.' :
              error}
          </p>
          <a href="/" style={styles.homeLink}>
            Go to FloorPlan Pro
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.logo}>FloorPlan Pro</span>
          <span style={styles.divider}>|</span>
          <span style={styles.projectTitle}>{shareData?.title || 'Shared Floor Plan'}</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.viewCount}>
            {shareData?.viewCount || 0} views
          </span>
          {shareData?.allowComments && (
            <button
              style={isAddingComment ? styles.addCommentButtonActive : styles.addCommentButton}
              onClick={() => setIsAddingComment(!isAddingComment)}
            >
              {isAddingComment ? 'Cancel' : '+ Add Comment'}
            </button>
          )}
        </div>
      </div>

      {/* Canvas Container */}
      <div
        style={styles.canvasContainer}
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        {isAddingComment && (
          <div style={styles.commentHint}>
            Click anywhere on the floor plan to add a comment
          </div>
        )}

        <FloorPlanCanvas
          ref={canvasRef}
          activeFloor={activeFloor}
          selectedItems={[]}
          scale={scale}
          offset={offset}
          tool="select"
          wallType="exterior"
          wallDetailLevel="simple"
          thinLines={false}
          isDrawing={false}
          drawStart={null}
          drawEnd={null}
          polylinePoints={[]}
          hatchPoints={[]}
          roomPoints={[]}
          selectedVertexIndex={null}
          moveBasePoint={null}
          movePreviewPoint={null}
          rotateCenter={null}
          rotateStartAngle={null}
          rotatePreviewAngle={null}
          snapGuidelines={[]}
          activeSnap={null}
          polarAngle={null}
          gripCursorPosition={null}
          units="imperial"
          gridSize='6"'
          showDimensions={true}
          showTempDimensions={false}
          showGrips={false}
          fontStyle="modern"
          isMobile={false}
          layers={layers}
          pdfLayer={shareData?.projectData?.pdfLayer || null}
          annotations={shareData?.projectData?.annotations || []}
          selectedAnnotationIds={[]}
          drawingAnnotation={null}
          annotationsAboveFloorPlan={true}
          onPointerDown={() => {}}
          onPointerMove={() => {}}
          onPointerUp={() => {}}
          onDoubleClick={() => {}}
          onWheel={() => {}}
        />

        {/* Comment Markers */}
        {comments.map((comment, index) => (
          <CommentMarker
            key={comment.id}
            number={index + 1}
            comment={comment}
            scale={scale}
            offset={offset}
          />
        ))}
      </div>

      {/* Comments Panel */}
      {shareData?.allowComments && comments.length > 0 && (
        <div style={styles.commentsPanel}>
          <h3 style={styles.commentsPanelTitle}>Comments ({comments.length})</h3>
          <div style={styles.commentsList}>
            {comments.map((comment, index) => (
              <div key={comment.id} style={styles.commentItem}>
                <div style={styles.commentNumber}>{index + 1}</div>
                <div style={styles.commentContent}>
                  <div style={styles.commentAuthor}>{comment.authorName}</div>
                  <div style={styles.commentText}>{comment.commentText}</div>
                  <div style={styles.commentTime}>
                    {comment.createdAt ? new Date(comment.createdAt).toLocaleDateString() : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Zoom Controls */}
      <div style={styles.zoomControls}>
        <button style={styles.zoomButton} onClick={() => setScale(s => Math.min(s * 1.2, 5))}>+</button>
        <span style={styles.zoomLevel}>{Math.round(scale * 100)}%</span>
        <button style={styles.zoomButton} onClick={() => setScale(s => Math.max(s * 0.8, 0.1))}>−</button>
      </div>

      {/* Comment Modal */}
      {showCommentModal && (
        <CommentModal
          onSubmit={handleAddComment}
          onClose={() => {
            setShowCommentModal(false);
            setCommentPosition(null);
          }}
        />
      )}
    </div>
  );
};

/**
 * CommentMarker - Displays a numbered marker on the canvas
 */
const CommentMarker = ({ number, comment, scale, offset }) => {
  const [showPopup, setShowPopup] = useState(false);

  const x = comment.positionX * scale + offset.x;
  const y = comment.positionY * scale + offset.y;

  return (
    <div
      style={{
        ...styles.marker,
        left: x,
        top: y,
      }}
      onClick={() => setShowPopup(!showPopup)}
    >
      {number}
      {showPopup && (
        <div style={styles.markerPopup}>
          <div style={styles.popupAuthor}>{comment.authorName}</div>
          <div style={styles.popupText}>{comment.commentText}</div>
          <div style={styles.popupTime}>
            {comment.createdAt ? new Date(comment.createdAt).toLocaleString() : ''}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * CommentModal - Modal for adding a new comment
 */
const CommentModal = ({ onSubmit, onClose }) => {
  const [authorName, setAuthorName] = useState('');
  const [commentText, setCommentText] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    onSubmit(authorName.trim(), commentText.trim());
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.modalTitle}>Add Comment</h3>
        <form onSubmit={handleSubmit}>
          <div style={styles.formField}>
            <label style={styles.label}>Your Name (optional)</label>
            <input
              type="text"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              placeholder="Anonymous"
              style={styles.input}
            />
          </div>
          <div style={styles.formField}>
            <label style={styles.label}>Comment</label>
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Enter your comment..."
              style={styles.textarea}
              rows={4}
              autoFocus
            />
          </div>
          <div style={styles.modalButtons}>
            <button type="button" style={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              style={styles.submitButton}
              disabled={!commentText.trim()}
            >
              Add Comment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    width: '100vw',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#080c10',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    background: 'rgba(15,20,28,0.98)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logo: {
    color: '#00c8ff',
    fontWeight: 'bold',
    fontSize: '16px',
  },
  divider: {
    color: '#4a5a6a',
  },
  projectTitle: {
    color: '#fff',
    fontSize: '14px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  viewCount: {
    color: '#6080a0',
    fontSize: '12px',
  },
  addCommentButton: {
    padding: '8px 16px',
    background: '#00c8ff',
    border: 'none',
    borderRadius: '6px',
    color: '#000',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  addCommentButtonActive: {
    padding: '8px 16px',
    background: 'rgba(255,100,100,0.2)',
    border: '1px solid rgba(255,100,100,0.5)',
    borderRadius: '6px',
    color: '#ff6464',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  canvasContainer: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
    cursor: 'grab',
  },
  commentHint: {
    position: 'absolute',
    top: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '10px 20px',
    background: 'rgba(0,200,255,0.2)',
    border: '1px solid rgba(0,200,255,0.4)',
    borderRadius: '8px',
    color: '#00c8ff',
    fontSize: '13px',
    zIndex: 100,
  },
  commentsPanel: {
    position: 'absolute',
    top: '70px',
    right: '16px',
    width: '280px',
    maxHeight: 'calc(100vh - 150px)',
    background: 'rgba(15,20,28,0.95)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  commentsPanelTitle: {
    padding: '12px 16px',
    margin: 0,
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
  },
  commentsList: {
    maxHeight: '400px',
    overflowY: 'auto',
  },
  commentItem: {
    display: 'flex',
    gap: '12px',
    padding: '12px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  },
  commentNumber: {
    width: '24px',
    height: '24px',
    background: '#00c8ff',
    borderRadius: '50%',
    color: '#000',
    fontSize: '12px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  commentContent: {
    flex: 1,
  },
  commentAuthor: {
    color: '#00c8ff',
    fontSize: '12px',
    fontWeight: '600',
    marginBottom: '4px',
  },
  commentText: {
    color: '#ccc',
    fontSize: '13px',
    lineHeight: '1.4',
  },
  commentTime: {
    color: '#6080a0',
    fontSize: '10px',
    marginTop: '4px',
  },
  zoomControls: {
    position: 'absolute',
    bottom: '20px',
    left: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(15,20,28,0.95)',
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  zoomButton: {
    width: '30px',
    height: '30px',
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  zoomLevel: {
    color: '#6080a0',
    fontSize: '12px',
    minWidth: '50px',
    textAlign: 'center',
  },
  marker: {
    position: 'absolute',
    width: '28px',
    height: '28px',
    background: '#00c8ff',
    borderRadius: '50%',
    border: '2px solid #fff',
    color: '#000',
    fontSize: '12px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transform: 'translate(-50%, -50%)',
    zIndex: 50,
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
  },
  markerPopup: {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    marginTop: '8px',
    width: '200px',
    background: 'rgba(15,20,28,0.98)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    padding: '12px',
    zIndex: 100,
    textAlign: 'left',
  },
  popupAuthor: {
    color: '#00c8ff',
    fontSize: '12px',
    fontWeight: '600',
    marginBottom: '4px',
  },
  popupText: {
    color: '#ccc',
    fontSize: '12px',
    lineHeight: '1.4',
    wordWrap: 'break-word',
  },
  popupTime: {
    color: '#6080a0',
    fontSize: '10px',
    marginTop: '8px',
  },
  loadingBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid rgba(0,200,255,0.2)',
    borderTop: '3px solid #00c8ff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#6080a0',
    marginTop: '16px',
    fontSize: '14px',
  },
  errorBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    padding: '20px',
  },
  errorIcon: {
    width: '60px',
    height: '60px',
    background: 'rgba(255,100,100,0.1)',
    border: '2px solid rgba(255,100,100,0.3)',
    borderRadius: '50%',
    color: '#ff6464',
    fontSize: '32px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  errorTitle: {
    color: '#fff',
    fontSize: '20px',
    fontWeight: '600',
    margin: '0 0 8px 0',
  },
  errorMessage: {
    color: '#6080a0',
    fontSize: '14px',
    maxWidth: '400px',
    margin: '0 0 24px 0',
  },
  homeLink: {
    color: '#00c8ff',
    textDecoration: 'none',
    fontSize: '14px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#1a2332',
    borderRadius: '12px',
    padding: '24px',
    width: '100%',
    maxWidth: '400px',
    margin: '20px',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  modalTitle: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: '600',
    margin: '0 0 20px 0',
  },
  formField: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    color: '#6080a0',
    fontSize: '12px',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    resize: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '20px',
  },
  cancelButton: {
    flex: 1,
    padding: '10px',
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '6px',
    color: '#8899aa',
    fontSize: '14px',
    cursor: 'pointer',
  },
  submitButton: {
    flex: 1,
    padding: '10px',
    background: '#00c8ff',
    border: 'none',
    borderRadius: '6px',
    color: '#000',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

// Add keyframe animation for spinner
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default ShareViewer;
